/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, HostBinding, OnChanges, ElementRef, Renderer2 } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { CMSEvent } from 'render/models/cms-event.model';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';

import { getWysiwygAligmentClasses } from './piece-image-text-utils';


@Component({
  selector: 'piece-image-text-render',
  templateUrl: './piece-image-text-render.component.html',
  styleUrls: ['./piece-image-text-render.component.scss']
})
export class PieceImageTextRenderComponent
  extends BaseRenderComponent<any>
  implements OnChanges {

  @HostBinding('class') public aligmentClasses: string | undefined;

  public orientation$!: Observable<string>;

  constructor(
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly productService: ProductsService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly elRef: ElementRef,
    private readonly renderer: Renderer2
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  public ngOnChanges() {
    if (this.piece) {
      this.aligmentClasses = getWysiwygAligmentClasses(this.piece);
    }
    const { objectFit, objectPosition } = this.settings.styles;

    if (!!objectFit) {
      this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectFit', `${objectFit}`, 2);
    }

    if (!!objectPosition) {
      this.renderer.setStyle(this.elRef.nativeElement, '--imageObjectPosition', `${objectPosition}`, 2);
    }
  }

  public getPortraitImageSrc(): LazyDataInterface | undefined {
    const image = this.settings.general.portraitImage.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef)
    };
    return {
      src: this.assetsSharedService.getAssetUrl(image, assetsUrl, akamaiOptions),
      width: this.settings.general.portraitImage.width,
      height: this.settings.general.portraitImage.height
    };
  }

  public getLandscapeImageSrc(): LazyDataInterface | undefined {
    const image = this.settings.general.landscapeImage.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef)
    };
    return {
      src: this.assetsSharedService.getAssetUrl(image, assetsUrl, akamaiOptions),
      width: this.settings.general.landscapeImage.width,
      height: this.settings.general.landscapeImage.height
    };
  }

  @HostBinding('class.marquee')
  public get isMarqueeTextAnimationEnabled(): boolean {
    return !!this.settings.general?.marquee?.marqueeStatus;
  }

  @HostBinding('style.--marquee-duration')
  public get marqueeTextDuration(): string | undefined {
    if (this.isMarqueeTextAnimationEnabled) {
      if (this.renderByDeviceService.isMobile) {
        return this.marqueeDurationMobileValue ? `${this.marqueeDurationMobileValue}s` : '5s';
      }
      return this.marqueeDurationDesktopValue ? `${this.marqueeDurationDesktopValue}s` : '20s';
    }
    return undefined;
  }

  public get marqueeDurationDesktopValue(): number | undefined {
    return this.settings.general?.marquee?.marqueeDurationDesktop;
  }

  public get marqueeDurationMobileValue(): number | undefined {
    return this.settings.general?.marquee?.marqueeDurationMobile;
  }

  public onRenderLinkClick(productInfo: { productId: string | undefined; categoryId: string | undefined }): void {
    if (productInfo.productId) {
      this.productService.productDetail(productInfo.productId, productInfo.categoryId).pipe(
        take(1)
      ).subscribe((product) => {
        const cmsEvent: CMSEvent<Product> = {
          name: 'productLink',
          type: 'click',
          data: product
        };
        this.cmsRenderEventsService.emitEvent(cmsEvent);
      });
    }
  }

}
